import { template as template_ba65ec8059234b58859ec0111059d3f2 } from "@ember/template-compiler";
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { readRecentNameSearches, rememberNameSearch, removeRecentNameSearch } from 'client-portal/utils/clinicianSearch/recent-name-searches';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import AttachTooltip from 'ember-attacher/components/attach-tooltip';
import Component from '@glimmer/component';
import RequestClinicianSearchFiltersPanel from '../filters/panel';
import styles from './name.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import t from 'ember-intl/helpers/t';
export default class RequestClinicianSearchFacetFiltersName extends Component {
    @service
    intl;
    @service
    clinicianSearch;
    @service
    practiceStorage;
    @service
    media;
    styles = styles;
    @tracked
    nameDraft = '';
    @tracked
    recentNames = [];
    @tracked
    recentNameStatusByKey = {};
    constructor(){
        super(...arguments);
        this.nameDraft = this.clinicianSearch.query.name ?? '';
        this.recentNames = readRecentNameSearches(this.practiceStorage);
    }
    get filterOptionsLoadingAriaLabel() {
        return this.intl.t('booking.clinicianSearch.filterDetail.filterOptionsLoading');
    }
    get hasRecentNames() {
        return this.recentNames.length > 0;
    }
    mapRecentNameStatuses = (resolveStatus)=>Object.fromEntries(this.recentNames.map((name)=>[
                name,
                resolveStatus(name)
            ]));
    refreshRecentNameStatuses = async ()=>{
        let routeParams = this.clinicianSearch.query.toRouteParams();
        try {
            this.recentNameStatusByKey = this.mapRecentNameStatuses((name)=>this.clinicianSearch.sendAll.probeName(routeParams, name));
        } catch  {
            this.recentNameStatusByKey = this.mapRecentNameStatuses(()=>'no');
        }
    };
    recentNameRowDisabled = (name)=>{
        return this.recentNameStatusByKey[name] === 'no';
    };
    applyName = (rawName)=>{
        let name = String(rawName ?? '').trim();
        if (name.length) {
            rememberNameSearch(this.practiceStorage, name);
        }
        this.recentNames = readRecentNameSearches(this.practiceStorage);
        this.clinicianSearch.query.applyFacetFilter('name', name);
        this.clinicianSearch.mobile.backFromDetail();
    };
    apply = ()=>{
        this.applyName(this.nameDraft);
    };
    setNameDraft = (event)=>{
        this.nameDraft = event.target.value;
    };
    handleSearchKeydown = (event)=>{
        if (event.key === 'Enter') {
            event.preventDefault();
            this.apply();
        }
    };
    removeRecent = (value, event)=>{
        event?.preventDefault?.();
        event?.stopPropagation?.();
        this.recentNames = removeRecentNameSearch(this.practiceStorage, value);
    };
    removeRecentAriaLabel = (name)=>this.intl.t('booking.clinicianSearch.filterDetail.removeRecentNameAria', {
            name
        });
    recentDisabledHintId = (name)=>{
        let key = String(name ?? '').trim().toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        return key ? `clinician-search-name-recent-disabled-${key}` : null;
    };
    get recentDisabledHint() {
        return this.intl.t('booking.clinicianSearch.filterDetail.disabledFilterOption');
    }
    showRecentDisabledHint = (recent)=>{
        return !this.media.isMdDown && this.recentNameRowDisabled(recent);
    };
    static{
        template_ba65ec8059234b58859ec0111059d3f2(`
    <RequestClinicianSearchFiltersPanel
      @class={{this.styles.component}}
      @facetKey="name"
      @isLoading={{this.clinicianSearch.sendAll.isLoading}}
      @loadingAriaLabel={{this.filterOptionsLoadingAriaLabel}}
      @onApply={{this.apply}}
      @onLoad={{this.refreshRecentNameStatuses}}
      data-test-clinician-search-filter-name-options
    >
      <div class="name-header">
        <p
          class="filter-section-title"
          id="clinician-search-name-filter-title"
          data-test-clinician-search-filter-name-panel-title
        >{{t "booking.clinicianSearch.filterDetail.nameFilterPanelTitle"}}</p>

        <div class="name-search-row">
          <span class="name-search-icon">{{svgJar
              "therapist-name-search"
              aria-hidden="true"
            }}</span>
          <input
            class="name-search-input"
            type="search"
            autocomplete="off"
            aria-label={{t "booking.clinicianSearch.filterDetail.nameSearchPlaceholder"}}
            placeholder={{t "booking.clinicianSearch.filterDetail.nameSearchPlaceholder"}}
            value={{this.nameDraft}}
            data-test-clinician-search-filter-name-input
            {{on "input" this.setNameDraft}}
            {{on "keydown" this.handleSearchKeydown}}
          />
        </div>
      </div>

      <div class="body">
        {{#if this.hasRecentNames}}
          <div
            class="name-recent-block"
            role="group"
            aria-labelledby="clinician-search-name-recent-heading"
          >
            <p class="name-recent-heading" id="clinician-search-name-recent-heading">{{t
                "booking.clinicianSearch.filterDetail.recentSearchHeading"
              }}</p>
            <ul class="name-recent-list" role="list">
              {{#each this.recentNames as |recent|}}
                <li class="name-recent-item">
                  <div class="name-recent-row">
                    <button
                      class="name-recent-button"
                      type="button"
                      disabled={{this.recentNameRowDisabled recent}}
                      aria-describedby={{if
                        (this.recentNameRowDisabled recent)
                        (this.recentDisabledHintId recent)
                      }}
                      data-test-clinician-search-filter-name-recent={{recent}}
                      {{on "click" (fn this.applyName recent)}}
                    >
                      {{recent}}
                    </button>
                    {{#if (this.showRecentDisabledHint recent)}}
                      <span id={{this.recentDisabledHintId recent}} class="visually-hidden">
                        {{this.recentDisabledHint}}
                      </span>
                      <AttachTooltip
                        @placement="right"
                        @class="tooltip align-left filter-disabled-tooltip-popover"
                        data-test-clinician-search-filter-name-recent-tooltip={{recent}}
                      >
                        {{this.recentDisabledHint}}
                      </AttachTooltip>
                    {{/if}}
                    <button
                      class="name-recent-remove-button"
                      type="button"
                      aria-label={{this.removeRecentAriaLabel recent}}
                      data-test-clinician-search-filter-name-recent-remove={{recent}}
                      {{on "click" (fn this.removeRecent recent)}}
                    >
                      {{svgJar "x-circ" aria-hidden="true"}}
                    </button>
                  </div>
                </li>
              {{/each}}
            </ul>
          </div>
        {{/if}}
      </div>
    </RequestClinicianSearchFiltersPanel>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
